import { Injectable } from '@angular/core';
import { RxState } from '@rx-angular/state';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, switchMap } from 'rxjs';

import { whenTruthy } from 'ish-core/utils/operators';

import { CustomDotCmsPage } from '../models/page/custom-dot-cms-page.model';
import { CustomDotCmsQueryParamsMapper } from '../models/page/custom-dot-cms-query-params-mapper';
import { DotCmsContentDepth, DotCmsPageRequestParameters } from '../services/dot-cms-resource/dot-cms-resource.service';

import { DotCmsFacade } from './dot-cms.facade';

export interface DotCMSPageContext {
  pageURI: string;
  depth: DotCmsContentDepth;
  page: CustomDotCmsPage;
  language: string;
  personaId: string;
  params?: DotCmsPageRequestParameters;
}

@Injectable({ providedIn: 'root' })
export class DotCmsPageContextFacade extends RxState<DotCMSPageContext> {
  protected pageURI$ = new BehaviorSubject<string>(undefined);
  protected depth$ = new BehaviorSubject<DotCmsContentDepth>(2);
  protected params$ = new BehaviorSubject<DotCmsPageRequestParameters>(undefined);
  constructor(protected dotCmsFacade: DotCmsFacade) {
    super();
    this.set({
      pageURI: undefined,
      depth: 2,
      personaId: undefined,
    });

    this.connect('pageURI', this.pageURI$);
    this.connect('depth', this.depth$);
    this.connect('params', this.dotCmsFacade.getQueryParams$);

    this.connect(
      'page',
      combineLatest([
        this.select('pageURI').pipe(whenTruthy()),
        this.select('depth'),
        this.dotCmsFacade.getPersonaId$(),
        this.select('params'),
        this.dotCmsFacade.isDotCmsReadyForApiCall$,
      ]).pipe(
        filter(([, , , , canMakeApiCall]) => canMakeApiCall),
        distinctUntilChanged(isEqual),
        switchMap(([uri, depth, personaId, queryParams]) => {
          const params = CustomDotCmsQueryParamsMapper.queryParamsMapper(queryParams);
          this.dotCmsFacade.initEditMode(uri);
          return this.dotCmsFacade.getPageByURI$(
            uri,
            {
              depth,
              personaId: params.personaId || personaId,
              variantName: params.variantName,
              language: params.language,
            },
            true
          );
        })
      )
    );
  }

  getPage$() {
    return this.select('page').pipe(whenTruthy());
  }

  getPageLayoutBody$() {
    return this.getPage$().pipe(map(page => page.pageLayout.body));
  }

  getPageMetaInfo$() {
    return this.getPage$().pipe(map(page => page.metaInfo));
  }

  getCurrentPageURI$() {
    return this.getPage$().pipe(map(page => page.metaInfo.pageURI));
  }

  getUrlContentMap$() {
    return this.getPage$().pipe(map(page => page?.urlContentMap));
  }
}
